import React, { useLayoutEffect, useState } from 'react';
import { RouteObject, useLocation, useLoaderData } from 'react-router-dom';
import { usePrevious } from './usePrevious';
import { useAppDispatch } from '../../stores/redux.store';
import type { ServerSideRedirect } from './useGlobalPageState';
import { ServerErrorException } from '../fetch-api';
import { buildInitialState, PreloadedAppState } from '../init-app-state';
import { resetAppState, setGlobalStateLoading } from '../../actions/app-actions';
import { ServerError } from '../../types';
import { captureErrorMessage } from '../analytics/error-reporting';
import { setServerError } from '../../actions/server-error-actions';

const isRedirect = (
  data: PreloadedAppState | ServerSideRedirect | null
): data is ServerSideRedirect => Boolean(data && 'redirectTo' in data);

// Later we will use this to extract page state, but for now we just sync with Redux, same as useGlobalPageState hook
export const usePageStateReduxSyncer = () => {
  const [isPageStateSyncedRedux, setIsPageStateSyncedRedux] = useState(false);
  const location = useLocation();
  const previousLocation = usePrevious(location.pathname);

  const data = useLoaderData() as PreloadedAppState | ServerSideRedirect;
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (previousLocation === location.pathname) {
      // Component re-renders can cause this hook to reset the Redux app state unnecessarily.
      // This condition ensures we only ever fetch state and reset Redux when only the pathname
      // and not the search, nor other route states, changes.
      return;
    }

    // No data could mean Feature Gate is disabled and useGlobalPageState is handling the state
    // Or there was a parsing error in the JSON response in `fetchPageState`
    if (!data) {
      // TODO: throw descriptive error here for handling in fine-grained error boundaries KIRBY-9124
      setIsPageStateSyncedRedux(true);
      return;
    }

    if (isRedirect(data)) {
      window.location.assign(data.redirectTo);
      dispatch(setGlobalStateLoading(true));
      return;
    }

    setIsPageStateSyncedRedux(false);
    buildInitialState(data)
      .then(fullState => {
        dispatch(resetAppState(fullState.initialStoreState));
      })
      .catch(error => {
        let errorType: ServerError = { type: 'internal_server_error' };
        if (error instanceof ServerErrorException) {
          errorType = error.errorType;
          captureErrorMessage(`Failed to load AppState in Bifrost: ${error.message}`);
        } else {
          captureErrorMessage(`Unexpected error while loading AppState in Bifrost: ${error}`);
        }
        dispatch(setServerError(errorType));
      })
      .finally(() => setIsPageStateSyncedRedux(true));
  }, [data, dispatch, location.pathname, previousLocation]);

  return { isPageStateSyncedRedux };
};

export const PageStateReduxSyncer = ({
  children = () => null, // Default to null for syncing redux when temporaryRedirectLoader is used
}: {
  children?: () => React.ReactNode;
}) => {
  const { isPageStateSyncedRedux } = usePageStateReduxSyncer();
  // This component is used to sync the page state with the Redux store which should take a few milliseconds.
  // Visual loading state is not required, the data loading is handled by React Router's loader.
  return isPageStateSyncedRedux ? children() : null;
};

export const wrapRoutesWithPageStateReduxSyncer = (routes: RouteObject[]): RouteObject[] => {
  return routes.map(route => {
    if (route.loader && route.element) {
      const element = route.element;
      route.element = <PageStateReduxSyncer>{() => element}</PageStateReduxSyncer>;
    }

    // Recursively wrap child routes with PageStateReduxSyncer
    if (route.children) {
      route.children = wrapRoutesWithPageStateReduxSyncer(route.children);
    }

    return route;
  });
};

/**
 * Migration hook: attempts to read a value from React Router's loader data,
 * falling back to Redux if the value is not available.

 * TODO: PreloadedAppState and RootState have different shapes. Some selectors
 * will return undefined from loader data today (e.g. state.login?.isUsernameChecked
 * only exists in Redux). As we remove Redux state from the app, this hook will gradually start returning loader values.

  * Replace Redux useAppSelector:
  * - const isUsernameChecked = useAppSelector(state => state.login?.isUsernameChecked)
  * + const isUsernameChecked = usePageStateLoader(state => state.login?.isUsernameChecked)

export function usePageStateLoader<T>(selector: (state: RootState) => T): T {
  const analyticsClient = useAnalyticsClient();
  const loaderData = useLoaderData() as PreloadedAppState | ServerSideRedirect | null;
  const reduxValue = useAppSelector(selector);

  if (loaderData && !isRedirect(loaderData)) {
    try {
      const loaderValue = selector(loaderData);
      if (loaderValue !== undefined) {
        return loaderValue;
      }
    } catch {
      // Selector shape mismatch - fall through to Redux
    }
  }

  analyticsClient.trackingEvent({
    action: 'fallback',
    subject: 'pageStateReduxSyncer',
    subjectId: selector.toString(),
  });

  return reduxValue;
}
*/
